/**
 * GpsDisclosure — One-time location consent banner.
 *
 * Shown before the first GPS capture (clock-in) to disclose that
 * location data is recorded and stored. Required for CCPA/privacy compliance.
 *
 * Shows once per device (acknowledged state persisted in localStorage).
 */

import { useState } from 'react';

const STORAGE_KEY = 'twg:time:gpsDisclosureAcknowledged';

function isAcknowledged(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false; // localStorage unavailable — show disclosure
  }
}

function acknowledge(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // localStorage unavailable — degrade gracefully
  }
}

interface GpsDisclosureProps {
  /** Called after user acknowledges the disclosure */
  onAcknowledge: () => void;
}

export function GpsDisclosure({ onAcknowledge }: GpsDisclosureProps) {
  const [acknowledged, setAcknowledged] = useState(isAcknowledged);

  if (acknowledged) return null;

  const handleAcknowledge = () => {
    acknowledge();
    setAcknowledged(true);
    onAcknowledge();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
      <div
        className="bg-white w-full rounded-t-2xl px-6 py-6 animate-slide-up safe-bottom"
        onClick={e => e.stopPropagation()}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-2xl">📍</span>
            <h2 className="text-lg font-display font-semibold text-forest-900">Location Recording</h2>
          </div>

          <p className="text-sage-700 text-sm leading-relaxed">
            When you clock in and out, your location is recorded to verify attendance at job sites.
            This data is stored securely and only visible to your manager for timesheet review.
          </p>

          <p className="text-sage-500 text-xs">
            You can disable location services in your device settings at any time.
            Clock entries without location will be flagged for manual review.
          </p>

          <button
            onClick={handleAcknowledge}
            className="w-full bg-forest-700 text-white rounded-2xl px-4 py-3 font-medium active:scale-95 transition-transform"
          >
            I understand
          </button>
        </div>
      </div>
    </div>
  );
}
